import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { jobsService } from "@/api/services/jobs.service";
import { EmptyState, LoopingImage } from "@/components/common";
import { useAlert } from "@/hooks/useAlert";
import { formatDateOnlyDisplay } from "@/utils/dateTime";
import { formatJobAddress } from "@/utils/address";
import styles from "./ClientJobsPage.module.css";
/** Normalize status to snake_case for consistent mapping (handles camelCase API responses) */
const normalizeStatus = (s) => (s ?? "")
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .toLowerCase()
    .replace(/-/g, "_");
// Map backend job status to client view status
// In Progress: scheduled, in_progress, new_project, not_started
// On Hold: on_hold
// Completed: completed, cancelled
const mapJobStatus = (status) => {
    const s = normalizeStatus(status);
    switch (s) {
        case "in_progress":
        case "scheduled":
        case "new_project":
        case "not_started":
            return "in_progress";
        case "on_hold":
            return "on_hold";
        case "completed":
        case "invoiced":
        case "cancelled":
            return "completed";
        default:
            return "in_progress";
    }
};
const LocationIcon = () => (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("path", { d: "M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" }), _jsx("circle", { cx: "12", cy: "10", r: "3" })] }));
const CalendarIcon = () => (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("rect", { x: "3", y: "4", width: "18", height: "18", rx: "2", ry: "2" }), _jsx("line", { x1: "16", y1: "2", x2: "16", y2: "6" }), _jsx("line", { x1: "8", y1: "2", x2: "8", y2: "6" }), _jsx("line", { x1: "3", y1: "10", x2: "21", y2: "10" })] }));
const ClockIcon = () => (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("circle", { cx: "12", cy: "12", r: "10" }), _jsx("polyline", { points: "12 6 12 12 16 14" })] }));
const ToolIcon = () => (_jsx("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: _jsx("path", { d: "M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" }) }));
const formatDate = (dateString) => formatDateOnlyDisplay(dateString ?? undefined);
const formatTimeRange = (startTime, endTime) => {
    if (!startTime && !endTime)
        return "TBD";
    return `${startTime || "TBD"} - ${endTime || "TBD"}`;
};
const formatDuration = (days) => {
    if (!days)
        return "TBD";
    return days === 1 ? "1 Day" : `${days} Days`;
};
export const ClientJobsPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { alert } = useAlert();
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [cancellingJob, setCancellingJob] = useState(null);
    // Fetch jobs for the current client
    const fetchJobs = useCallback(async () => {
        if (!user?.id)
            return;
        try {
            setLoading(true);
            setError(null);
            const response = await jobsService.getJobsByContact(user.id);
            setJobs(response.data);
        }
        catch (err) {
            console.error("Error fetching jobs:", err);
            setError("Failed to load jobs. Please try again.");
        }
        finally {
            setLoading(false);
        }
    }, [user?.id]);
    useEffect(() => {
        fetchJobs();
    }, [fetchJobs]);
    // Scroll to section when hash is present (e.g. from Client Home click)
    useEffect(() => {
        const hash = location.hash?.slice(1);
        if (!hash)
            return;
        const validIds = ["in-progress", "on-hold", "completed"];
        if (!validIds.includes(hash))
            return;
        const timer = setTimeout(() => {
            document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
        }, 100);
        return () => clearTimeout(timer);
    }, [location.hash]);
    const jobsBySection = useMemo(() => {
        const grouped = {
            in_progress: [],
            on_hold: [],
            completed: [],
        };
        jobs.forEach((job) => {
            const mappedStatus = mapJobStatus(job.status);
            grouped[mappedStatus].push(job);
        });
        return grouped;
    }, [jobs]);
    const handleViewJob = (jobId) => {
        navigate(`/client/jobs/${jobId}`);
    };
    const handleCancelJob = async (jobId) => {
        setCancellingJob(jobId);
        try {
            await jobsService.cancelJob(jobId);
            await fetchJobs();
        }
        catch (err) {
            console.error("Error cancelling job:", err);
            await alert("Failed to cancel job. Please try again.");
        }
        finally {
            setCancellingJob(null);
        }
    };
    const inProgressJobs = jobsBySection.in_progress;
    const onHoldJobs = jobsBySection.on_hold;
    const completedJobs = jobsBySection.completed;
    const renderJobCard = (job, index) => {
        const mappedStatus = mapJobStatus(job.status);
        return (_jsxs("div", { className: styles.jobCard, children: [_jsx("div", { className: styles.jobImage, children: job.imageUrl ? (_jsx("img", { src: job.imageUrl, alt: `Job ${job.jobNumber}`, onError: (e) => {
                            e.target.src =
                                "/images/Defit Social Media 3.png";
                        } })) : index !== undefined ? (_jsx(LoopingImage, { imagePrefix: "/images/request-quote-default", count: index + 1, fallbackImage: "/images/empty-state-job.png", className: styles.jobLoopingImage })) : (_jsx("div", { className: styles.jobImagePlaceholder, children: _jsx(ToolIcon, {}) })) }), _jsxs("div", { className: styles.jobDetails, children: [_jsx("div", { className: styles.jobHeader, children: _jsxs("div", { className: styles.jobTitle, children: ["Job #", job.jobNumber] }) }), _jsxs("div", { className: styles.jobInfoGrid, children: [_jsxs("div", { className: styles.jobInfoItem, children: [_jsx(LocationIcon, {}), _jsx("span", { children: formatJobAddress(job, "TBD") })] }), _jsxs("div", { className: styles.jobInfoItem, children: [_jsx(CalendarIcon, {}), _jsx("span", { children: formatDate(job.scheduledDate || null) })] }), _jsxs("div", { className: styles.jobInfoItem, children: [_jsx(ClockIcon, {}), _jsx("span", { children: formatTimeRange(job.startTime || null, job.endTime || null) })] }), _jsxs("div", { className: styles.jobInfoItem, children: [_jsx(ClockIcon, {}), _jsx("span", { children: formatDuration(job.durationDays) })] })] }), _jsxs("div", { className: styles.jobActions, children: [_jsxs("button", { className: styles.viewJobBtn, onClick: () => handleViewJob(job.id), children: [_jsx(ToolIcon, {}), "VIEW JOB"] }), mappedStatus !== "completed" && (_jsx("button", { className: styles.cancelBtn, onClick: () => handleCancelJob(job.id), disabled: cancellingJob === job.id, children: cancellingJob === job.id ? "..." : "CANCEL" }))] })] })] }, job.id));
    };
    // Loading state
    if (loading) {
        return (_jsx("div", { className: styles.page, children: _jsx("div", { className: styles.emptyState, children: _jsx("p", { children: "Loading jobs..." }) }) }));
    }
    // Error state
    if (error) {
        return (_jsx("div", { className: styles.page, children: _jsxs("div", { className: styles.emptyState, children: [_jsx("p", { children: error }), _jsx("button", { onClick: fetchJobs, style: {
                            marginTop: "var(--space-md)",
                            padding: "10px 20px",
                            border: "none",
                            borderRadius: "var(--radius-md)",
                            background: "var(--color-primary)",
                            color: "white",
                            cursor: "pointer",
                        }, children: "Try Again" })] }) }));
    }
    return (_jsx(_Fragment, { children: _jsx("div", { className: styles.page, children: _jsxs("div", { className: styles.sectionsGrid, children: [_jsxs("section", { id: "in-progress", className: styles.section, children: [_jsx("div", { className: styles.sectionHeader, children: _jsx("h2", { className: styles.sectionTitle, children: "In Progress" }) }), inProgressJobs.length === 0 ? (_jsx("div", { className: styles.jobsContainer, children: _jsx(EmptyState, { image: "/images/empty-state-quote.png", heading: "No jobs in progress", text: "Active projects will appear here while work is underway." }) })) : (_jsx("div", { className: styles.jobsContainer, children: _jsx("div", { className: styles.jobsGrid, children: inProgressJobs.map((job, i) => renderJobCard(job, i)) }) }))] }), _jsxs("section", { id: "on-hold", className: styles.section, children: [_jsx("div", { className: styles.sectionHeader, children: _jsx("h2", { className: styles.sectionTitle, children: "On Hold" }) }), onHoldJobs.length === 0 ? (_jsx("div", { className: styles.jobsContainer, children: _jsx(EmptyState, { image: "/images/empty-state-job.png", heading: "No jobs on hold", text: "Projects paused or awaiting action will be listed here." }) })) : (_jsx("div", { className: styles.jobsContainer, children: _jsx("div", { className: styles.jobsGrid, children: onHoldJobs.map((job, i) => renderJobCard(job, i)) }) }))] }), _jsxs("section", { id: "completed", className: styles.section, children: [_jsx("div", { className: styles.sectionHeader, children: _jsx("h2", { className: styles.sectionTitle, children: "Complete" }) }), completedJobs.length === 0 ? (_jsx("div", { className: styles.jobsContainer, children: _jsx(EmptyState, { image: "/images/empty-state-complete.png", heading: "No completed jobs yet", text: "Finished projects will appear here once completed." }) })) : (_jsx("div", { className: styles.jobsContainer, children: _jsx("div", { className: styles.jobsGrid, children: completedJobs.map((job, i) => renderJobCard(job, i)) }) }))] })] }) }) }));
};
